import { NAVIGATION_PAGE_CONFIGS } from '@/data/navigationPageConfigs';
import { mainNavImages, caseStudyImages } from '@/data/pageImages';
import { blogPosts } from '@/data/blog';
import crossroadsImage from '@/assets/work-carousel/crossroads.png';
import straataImage from '@/assets/work-carousel/straata.png';
import driverSchedulingImage from '@/assets/work-carousel/driver-scheduling.png';
import babelcastImage from '@/assets/work-carousel/babelcast.png';
import estatezaImage from '@/assets/work-carousel/estateza.png';
import freightInvoicingImage from '@/assets/work-carousel/freight-invoicing.png';
import propaneportImage from '@/assets/cases/pport/0.png';

const ASSET_REGEX = /(https?:\/\/[^\s]+\.(?:png|jpe?g|webp|gif|svg|avif|mp4|webm|mov|m4v)(?:\?.*)?$)|(^\/?[^\s]+\.(?:png|jpe?g|webp|gif|svg|avif|mp4|webm|mov|m4v)(?:\?.*)?$)/i;

type Collectable = Record<string, unknown> | unknown[];

const workCarouselAssets = [
  crossroadsImage,
  straataImage,
  driverSchedulingImage,
  babelcastImage,
  estatezaImage,
  freightInvoicingImage,
  propaneportImage,
];

const routeSpecificAssets: Record<string, string[]> = {
  '/': [
    '/videos/drivin.mp4',
    '/videos/red.mp4',
    '/videos/narrative.mp4',
  ],
  '/services': [
    'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=80',
    'https://images.unsplash.com/photo-1522252234503-e356532cafd5?auto=format&fit=crop&w=1400&q=80',
  ],
  '/work': workCarouselAssets,
  '/nexus': [
    'https://images.unsplash.com/photo-1545239351-1141bd82e8a6?auto=format&fit=crop&w=1400&q=80',
    'https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?auto=format&fit=crop&w=1400&q=80',
  ],
  '/key-services/replicant': [
    '/images/blog/content/universal-context.png',
    '/images/blog/content/agentic.png',
    '/images/blog/content/chaining.png',
    '/images/blog/content/info-velocity.png',
    '/images/blog/content/llm-reasoning-1.png',
    '/images/blog/content/llm-reasoning-2.png',
  ],
};

const shouldCollect = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed || trimmed.startsWith('data:')) return false;
  return ASSET_REGEX.test(trimmed);
};

const collectFromValue = (value: unknown, target: Set<string>) => {
  if (!value) return;

  if (Array.isArray(value)) {
    value.forEach((item) => collectFromValue(item, target));
    return;
  }

  if (typeof value === 'object') {
    Object.values(value as Collectable).forEach((item) => collectFromValue(item, target));
    return;
  }

  if (shouldCollect(value)) {
    target.add(value.trim());
  }
};

const buildRouteManifest = () => {
  const manifest: Record<string, string[]> = {};

  Object.entries(NAVIGATION_PAGE_CONFIGS).forEach(([route, config]) => {
    const routeAssets = new Set<string>();
    collectFromValue(config, routeAssets);

    const extras = routeSpecificAssets[route];
    if (extras) {
      extras.forEach((asset) => {
        if (shouldCollect(asset)) {
          routeAssets.add(asset.trim());
        }
      });
    }

    if (routeAssets.size > 0) {
      manifest[route] = Array.from(routeAssets);
    }
  });

  return manifest;
};

const buildGlobalManifest = () => {
  const assets = new Set<string>();

  collectFromValue(mainNavImages, assets);
  collectFromValue(caseStudyImages, assets);
  workCarouselAssets.forEach((asset) => collectFromValue(asset, assets));
  blogPosts.forEach((post) => collectFromValue(post, assets));
  Object.values(routeSpecificAssets).forEach((list) => {
    list.forEach((asset) => {
      if (shouldCollect(asset)) {
        assets.add(asset.trim());
      }
    });
  });

  return Array.from(assets);
};

export const routeImageManifest = buildRouteManifest();
export const globalImageManifest = buildGlobalManifest();

export const getRouteImages = (pathname: string): string[] => {
  return routeImageManifest[pathname] ?? [];
};
